import { Injectable, inject } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { EMPTY, from, of } from 'rxjs'
import { catchError, endWith, map, mergeMap, switchMap, tap } from 'rxjs/operators'
import { BusActions } from 'src/constants/bus-events'
import { BusService } from 'src/services/bus.service'
import { AESService } from 'src/services/data/aes.service'
import { ToasterService } from 'src/shared/toaster/toaster.service'
import { sendAndWatch } from 'src/shared/utils/send-and-watch'
import {
  aesFilingResolved,
  cancelAESExportReport,
  cancelAESExportReportFailure,
  cancelAESExportReportSuccess,
  createAESExportReport,
  createAESExportReportFailure,
  createAESExportReportSuccess,
  createAESExportRequestsInBatch,
  createAESInBatchFailure,
  createAESInBatchSuccess,
  setAesResponseStatus,
  setAesResponseStatusFailure,
  setAesResponseStatusSuccess
} from './aes.actions'

const AES_FAILED_HINT = 'Open AES requests on the deal page to see why.'

const isDialogDismissal = (error: unknown) =>
  error === 'cancel' || error === 'backdrop click' || error === 'escape key press'

@Injectable()
export class AesEffects {
  private actions$ = inject(Actions)
  private aes = inject(AESService)
  private toaster = inject(ToasterService)
  private bus = inject(BusService)


  createAESExportReport$ = createEffect(() => this.actions$.pipe(
    ofType(createAESExportReport),
    mergeMap(action =>
      from(this.aes.confirmCreate(action.aesNumberAlreadyAssigned)).pipe(
        catchError(error => {
          if (!isDialogDismissal(error)) console.error('Unable to confirm AES export report', error)
          return EMPTY
        }),
        switchMap(() => sendAndWatch(this.bus, this.toaster, {
          action: BusActions.AES_FILING,
          dealIds: [action.dealId],
          request: operationId => from(this.aes.create(action.dealId, action.aesConsigneeSource, operationId)),
          acceptedDealIds: () => [action.dealId],
          messages: { system: 'AES', subject: 'filing', submitted: 'AES filing submitted', failedHint: AES_FAILED_HINT },
        }).pipe(
          map(aes => createAESExportReportSuccess({ aes })),
          endWith(aesFilingResolved({ dealId: action.dealId })))),
        catchError(error => {
          console.error('Unable to create AES export report', error)
          this.toaster.error('Unable to create AES export report', error)
          return of(createAESExportReportFailure({ error }))
        })))))

  cancelAESExportReport$ = createEffect(() => this.actions$.pipe(
    ofType(cancelAESExportReport),
    mergeMap(action =>
      from(this.aes.confirmCancel()).pipe(
        catchError(error => {
          if (!isDialogDismissal(error)) console.error('Unable to confirm AES export report cancellation', error)
          return EMPTY
        }),
        switchMap(() => sendAndWatch(this.bus, this.toaster, {
          action: BusActions.AES_FILING,
          dealIds: [action.dealId],
          request: operationId => from(this.aes.cancel(action.reportId, action.dealId, action.reason, operationId)),
          acceptedDealIds: () => [action.dealId],
          messages: { system: 'AES', subject: 'cancellation', submitted: 'AES cancellation submitted', failedHint: AES_FAILED_HINT },
        }).pipe(
          map(aes => cancelAESExportReportSuccess({ aes })),
          endWith(aesFilingResolved({ dealId: action.dealId })))),
        catchError(error => {
          console.error('Unable to cancel AES export report', error)
          this.toaster.error('Unable to cancel AES export report', error)
          return of(cancelAESExportReportFailure({ error }))
        })))))

    createAESExportRequestsInBatch$ = createEffect(() => this.actions$.pipe(
      ofType(createAESExportRequestsInBatch),
      mergeMap(action =>
        from(this.aes.confirmCreateInBatch(action.aesNumberAlreadyAssigned)).pipe(
          catchError(error => {
            if (!isDialogDismissal(error)) console.error('Unable to confirm AES export reports', error)
            return EMPTY
          }),
          switchMap(() => sendAndWatch(this.bus, this.toaster, {
            action: BusActions.AES_FILING,
            dealIds: action.dealIds,
            request: operationId => from(this.aes.createInBatch(action.dealIds, operationId)),
            acceptedDealIds: result => (result?.created_reports || []).map(r => r.deal_id),
            messages: { system: 'AES', subject: 'filing', submitted: 'AES filings submitted', failedHint: AES_FAILED_HINT },
          }).pipe(
            map(result => createAESInBatchSuccess(result)),
            endWith(...action.dealIds.map(dealId => aesFilingResolved({ dealId }))))),
          catchError(error => {
            console.error('Unable to create AES export reports', error)
            this.toaster.error('Unable to create AES export reports, please refresh and retry individually')
            return of(createAESInBatchFailure({ error }))
          })))))

    setAesResponseStatus$ = createEffect(() => this.actions$.pipe(
      ofType(setAesResponseStatus),
      tap(({ status }) => this.toaster.progress(`Setting request status to ... ${status}`)),
      switchMap(action =>
        from(this.aes.setResponseStatus(action.reportId, action.status)).pipe(
          map(report => setAesResponseStatusSuccess(report)),
          tap(() => this.toaster.success('Updated response status successfully')),
          catchError(error => {
            console.error('Unable to set response status', error)
            if (!error.toasted) this.toaster.error('Unable to set response status', error.error || error )
            return of(setAesResponseStatusFailure({ error }))
          })))))
}
